import { CorporateAdmins } from '@platform/client';
import type { CohortData } from '@platform/client/schemas';
import { StatCard } from './StatCard';
import type { StatRow } from './types';

export const CohortStatsBox = ({
  cohortId,
  cohort,
}: {
  cohortId: number;
  cohort: CohortData;
}) => {
  const {
    data: applicantsData,
    isSuccess,
    isLoading,
    isError,
  } = CorporateAdmins.useGetApplicants(cohortId);

  const isTeamsCohort = cohort.type === 'Teams';

  if (isError) return <div>Error loading applicants</div>;
  if (isLoading || !isSuccess || !applicantsData) return <div>Loading...</div>;

  const { accepted, pending, rejected, dropped } = applicantsData;

  // Dropped applicants were accepted before dropping, so they count toward Accepted Participants.
  const acceptedParticipants = accepted.length + dropped.length;
  const completedApplications = acceptedParticipants + pending.length + rejected.length;

  const matched = accepted.filter((p) => !!p.matchData);
  const notMatched = accepted.filter((p) => !p.matchData);
  const projectProgress = accepted.filter((p) => p.matchStatus);

  const byStatus = (status: string) =>
    projectProgress.filter((p) => p.matchStatus === status).length;

  const stats: { title: string; total: number; rows: StatRow[] }[] = [
    {
      title: 'Completed Applications',
      total: completedApplications,
      rows: [
        { label: 'Accepted', value: acceptedParticipants, labelColor: 'text-success-500' },
        { label: 'Pending', value: pending.length, labelColor: 'text-warning-500' },
        { label: 'Rejected', value: rejected.length, labelColor: 'text-error-600' },
      ],
    },
    {
      title: 'Accepted Participants',
      total: acceptedParticipants,
      rows: [
        { label: 'Matched', value: matched.length, labelColor: 'text-success-500' },
        { label: 'Not Matched', value: notMatched.length, labelColor: 'text-warning-500' },
        { label: 'Dropped', value: dropped.length, labelColor: 'text-error-600' },
      ],
    },
    {
      title: 'Project Progress',
      total: projectProgress.length,
      rows: [
        ...(isTeamsCohort ?
          []
        : [{ label: 'Discovery', value: byStatus('discovery'), labelColor: 'text-error-600' }]),
        { label: 'Planning', value: byStatus('planning'), labelColor: 'text-brand-500' },
        { label: 'Action', value: byStatus('action'), labelColor: 'text-brand-600' },
        { label: 'Reflect', value: byStatus('reflect'), labelColor: 'text-success-500' },
        { label: 'Completed', value: byStatus('complete'), labelColor: 'text-success-600' },
      ],
    },
  ];

  return (
    <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
      {stats.map((s) => (
        <StatCard key={s.title} title={s.title} total={s.total} rows={s.rows} />
      ))}
    </div>
  );
};
